import { FormEvent } from "react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import Button from "../../../../../shared/components/Button";
import InfoTooltip from "../../../../../shared/components/InfoTooltip";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import {
  AuthUser,
  UserSidePreference,
  USER_CATEGORY_VALUES,
} from "../../../../auth/shared/types/auth";
import type { Club } from "../../../../clubs/shared/types/club";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

type ProfileOverviewTabProps = {
  user: AuthUser;
  dashboardTexts: typeof siteTexts.profile.user.dashboard;
  info: typeof siteTexts.common.info;
  open: boolean;
  categoryIsLocked: boolean;
  clubs: Club[];
  clubsLoading: boolean;
  sidePreferenceLoading: boolean;
  saving: boolean;
  hasProfileChanges: boolean;
  error: string;
  nameDraft: string;
  cpfDraft: string;
  phoneDraft: string;
  sexDraft: AuthUser["sex"] | "";
  categoryDraft: AuthUser["category"] | "";
  clubIdDraft: string;
  sidePreferenceDraft: UserSidePreference;
  onToggle: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onReset: () => void;
  onNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onSexChange: (value: AuthUser["sex"] | "") => void;
  onCategoryChange: (value: AuthUser["category"] | "") => void;
  onClubIdChange: (value: string) => void;
  onSidePreferenceChange: (value: UserSidePreference) => void;
  localizedCategoryLabel: (value: AuthUser["category"] | string) => string;
  memberSinceLabel: string;
};

export default function ProfileOverviewTab({
  user,
  dashboardTexts,
  info,
  open,
  categoryIsLocked,
  clubs,
  clubsLoading,
  sidePreferenceLoading,
  saving,
  hasProfileChanges,
  error,
  nameDraft,
  cpfDraft,
  phoneDraft,
  sexDraft,
  categoryDraft,
  clubIdDraft,
  sidePreferenceDraft,
  onToggle,
  onSubmit,
  onReset,
  onNameChange,
  onPhoneChange,
  onSexChange,
  onCategoryChange,
  onClubIdChange,
  onSidePreferenceChange,
  localizedCategoryLabel,
  memberSinceLabel,
}: ProfileOverviewTabProps) {
  return (
    <>
      <button
        type="button"
        id="profile-overview-mobile-button"
        className="user-profile-accordion-trigger user-profile-mobile-overview-trigger"
        aria-expanded={open}
        aria-controls="profile-overview-mobile-panel"
        onClick={onToggle}
      >
        <span className="user-profile-accordion-icon" aria-hidden="true">
          <span className="material-symbols-outlined">person</span>
        </span>
        <span className="user-profile-accordion-copy">
          <strong>{dashboardTexts.overviewTitle}</strong>
          <small>{dashboardTexts.overviewSubtitle}</small>
        </span>
        <span
          className="material-symbols-outlined user-profile-accordion-chevron"
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>
      <UserProfileSectionHeader
        className="user-profile-section-hero"
        icon="person"
        title={dashboardTexts.overviewTitle}
        description={dashboardTexts.overviewSubtitle}
      />

      <form
        id="profile-overview-mobile-panel"
        className="user-profile-form user-profile-info-form"
        onSubmit={onSubmit}
      >
        <section className="user-profile-info-section">
          <div className="user-profile-info-grid">
            <label className="user-profile-info-card">
              <span className="material-symbols-outlined" aria-hidden="true">
                person
              </span>
              <span className="user-profile-info-field">
                <small>{siteTexts.profile.user.labels.name}</small>
                <input
                  type="text"
                  value={nameDraft}
                  onChange={(event) => onNameChange(event.target.value)}
                  required
                />
              </span>
            </label>

            <label className="user-profile-info-card">
              <span className="material-symbols-outlined" aria-hidden="true">
                badge
              </span>
              <span className="user-profile-info-field">
                <small>{siteTexts.profile.user.labels.cpf}</small>
                <input
                  type="text"
                  value={cpfDraft}
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  maxLength={14}
                  readOnly
                  disabled
                />
              </span>
            </label>

            <label className="user-profile-info-card">
              <span className="material-symbols-outlined" aria-hidden="true">
                call
              </span>
              <span className="user-profile-info-field">
                <small>{siteTexts.profile.user.labels.phone}</small>
                <PhoneInput
                  defaultCountry="br"
                  value={phoneDraft}
                  onChange={onPhoneChange}
                  placeholder="+55 (11) 99999-9999"
                />
              </span>
            </label>

            <label className="user-profile-info-card">
              <span className="material-symbols-outlined" aria-hidden="true">
                wc
              </span>
              <span className="user-profile-info-field">
                <small>{siteTexts.profile.user.labels.sex}</small>
                <select
                  value={sexDraft}
                  onChange={(event) =>
                    onSexChange(event.target.value as AuthUser["sex"] | "")
                  }
                >
                  <option value="">{siteTexts.common.sex.notInformed}</option>
                  <option value="female">{siteTexts.common.sex.female}</option>
                  <option value="male">{siteTexts.common.sex.male}</option>
                  <option value="other">{siteTexts.common.sex.other}</option>
                </select>
              </span>
            </label>

            <label className="user-profile-info-card">
              <span className="material-symbols-outlined" aria-hidden="true">
                leaderboard
              </span>
              <span className="user-profile-info-field">
                <small className="info-label">
                  {dashboardTexts.stats.category}
                  <InfoTooltip content={info.currentCategory} />
                </small>
                <select
                  value={categoryDraft}
                  disabled={categoryIsLocked}
                  onChange={(event) =>
                    onCategoryChange(
                      event.target.value as AuthUser["category"] | "",
                    )
                  }
                >
                  {USER_CATEGORY_VALUES.map((item) => (
                    <option key={`profile-category-${item || "empty"}`} value={item}>
                      {item
                        ? localizedCategoryLabel(item)
                        : siteTexts.common.category.notInformed}
                    </option>
                  ))}
                </select>
              </span>
            </label>

            <label className="user-profile-info-card">
              <span className="material-symbols-outlined" aria-hidden="true">
                location_city
              </span>
              <span className="user-profile-info-field">
                <small>{siteTexts.profile.user.labels.club}</small>
                <select
                  value={clubIdDraft}
                  disabled={clubsLoading}
                  onChange={(event) => onClubIdChange(event.target.value)}
                >
                  <option value="">
                    {siteTexts.profile.user.labels.noClub}
                  </option>
                  {clubs.map((club) => (
                    <option key={`profile-club-${club.id}`} value={String(club.id)}>
                      {club.city && club.state
                        ? `${club.name} - ${club.city}/${club.state}`
                        : club.name}
                    </option>
                  ))}
                </select>
              </span>
            </label>

            <label className="user-profile-info-card">
              <span className="material-symbols-outlined" aria-hidden="true">
                swap_horiz
              </span>
              <span className="user-profile-info-field">
                <small>{siteTexts.profile.user.labels.sidePreference}</small>
                <select
                  value={sidePreferenceDraft}
                  disabled={sidePreferenceLoading}
                  onChange={(event) =>
                    onSidePreferenceChange(
                      event.target.value as UserSidePreference,
                    )
                  }
                >
                  <option value="ambos">
                    {siteTexts.profile.user.sidePreference.both}
                  </option>
                  <option value="esquerda">
                    {siteTexts.profile.user.sidePreference.left}
                  </option>
                  <option value="direita">
                    {siteTexts.profile.user.sidePreference.right}
                  </option>
                </select>
              </span>
            </label>

            <div className="user-profile-info-card">
              <span className="material-symbols-outlined" aria-hidden="true">
                event
              </span>
              <span className="user-profile-info-field">
                <small>{dashboardTexts.memberSince}</small>
                <strong>{memberSinceLabel}</strong>
              </span>
            </div>
          </div>
        </section>

        {error && <p className="status-message">{error}</p>}

        <div className="user-profile-actions">
          <Button
            type="button"
            variant="pill"
            onClick={onReset}
            disabled={saving || !hasProfileChanges}
          >
            {siteTexts.profile.user.cancel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !hasProfileChanges}
          >
            {saving
              ? siteTexts.profile.user.saving
              : siteTexts.profile.user.saveChanges}
          </Button>
        </div>
      </form>
    </>
  );
}
